import { createContext, useContext, useEffect, useState } from "react";

import { getPersonaTrends, getReport } from "../api/persona";

/**
 * Store for a single persona report's tab data.
 *
 * Both tabs' data (the report and its trends) is fetched once when the report is opened and kept
 * here, so switching tabs is instant and no tab refetches on click. A single AbortController per
 * load also cancels the duplicate request React StrictMode fires on mount in development, which was
 * causing the persona-trends endpoint to be hit twice.
 *
 * `usePersonaReportData()` returns null when there is no provider (e.g. the standalone
 * /persona/trends view), so tab components fall back to self-fetching.
 */
const PersonaReportDataContext = createContext(null);

export const usePersonaReportData = () => useContext(PersonaReportDataContext);

const loadingResource = { data: null, loading: true, error: false };
const idleResource = { data: null, loading: false, error: false };

export const PersonaReportDataProvider = ({ reportId, children }) => {
  const [report, setReport] = useState(reportId ? loadingResource : idleResource);
  const [trends, setTrends] = useState(loadingResource);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const load = () => {
      if (reportId) {
        setReport(loadingResource);
        getReport(reportId, { signal })
          .then((r) => setReport({ data: r.data, loading: false, error: false }))
          .catch(() => {
            if (!signal.aborted) setReport({ data: null, loading: false, error: true });
          });
      }

      setTrends(loadingResource);
      getPersonaTrends({ signal })
        .then((r) => setTrends({ data: r.data, loading: false, error: false }))
        .catch(() => {
          if (!signal.aborted) setTrends({ data: null, loading: false, error: true });
        });
    };

    load();
    // A token that arrives after mount (late login) should refill every tab.
    const onAuthChange = () => load();
    window.addEventListener("authChange", onAuthChange);
    return () => {
      controller.abort();
      window.removeEventListener("authChange", onAuthChange);
    };
  }, [reportId]);

  const value = { report, trends };
  return (
    <PersonaReportDataContext.Provider value={value}>{children}</PersonaReportDataContext.Provider>
  );
};
